<template>
    <article class="makeup-application-card" data-role-card="makeup">
        <div class="makeup-card-art" aria-hidden="true">
            <img class="makeup-card-portrait" :src="role.portrait" alt="" />
        </div>
        <div class="makeup-card-content">
            <header class="makeup-card-topline">
                <div>
                    <p>MAKEUP ARTIST</p>
                    <h3>{{ role.professionalTypeDescription }}</h3>
                </div>
                <div class="makeup-card-badges">
                    <span v-if="hasCampusCertification" class="makeup-campus-badge">校园</span>
                    <span class="makeup-status-badge" :class="statusPresentation.className">{{ statusPresentation.label }}</span>
                </div>
            </header>

            <p class="makeup-card-description">{{ cardDescription }}</p>
            <div class="makeup-card-action-row">
                <button
                    type="button"
                    class="makeup-card-action"
                    :class="{ 'makeup-card-action--quiet': cardAction === 'detail' }"
                    @click="handleCardAction">{{ cardActionLabel }}</button>
            </div>
        </div>

        <makeup-artist-application-dialog
            :visible="dialogVisible"
            @close="dialogVisible = false"
            @submitted="handleApplicationSubmitted" />

        <professional-application-detail-dialog
            professional-type="makeupArtist"
            :visible="detailDialogVisible"
            :loading="detailLoading"
            :error="detailError"
            :detail="applicationDetail"
            :show-resubmit="canResubmit"
            @close="closeApplicationDetail"
            @resubmit="openResubmit" />
    </article>
</template>

<script>
import request from "@/tool/request";
import API from "@/tool/api";
import {
    normalizeProfessionalApplicationDetail,
} from "@/tool/creatorCenter.cjs";
import {
    getResultData,
    getResultMessage,
    isResultSuccess,
} from "@/tool/result";
import MakeupArtistApplicationDialog from "./MakeupArtistApplicationDialog";
import ProfessionalApplicationDetailDialog from "./ProfessionalApplicationDetailDialog";

export default {
    name: "MakeupArtistApplicationCard",
    components: {
        MakeupArtistApplicationDialog,
        ProfessionalApplicationDetailDialog,
    },
    props: {
        role: {
            type: Object,
            required: true,
        },
        hasCampusCertification: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            applicationDetail: null,
            detailLoading: false,
            detailError: "",
            detailDialogVisible: false,
            dialogVisible: false,
        };
    },
    computed: {
        initialApplication() {
            return this.role.initialApplication || null;
        },
        initialApplicationId() {
            return this.initialApplication && this.initialApplication.id;
        },
        statusPresentation() {
            const status = Number(this.role.currentStatus);
            if (status === 1) return { label: "已认证", className: "approved" };
            if (status === 2) return { label: "已暂停", className: "review" };
            if (status === 3) return { label: "后台停用", className: "withdrawn" };
            return { label: "未生效", className: "ready" };
        },
        cardAction() {
            const actions = {
                SUBMIT_INITIAL_APPLICATION: "apply",
                VIEW_INITIAL_APPLICATION: "detail",
                VIEW_MATERIALS: "materials",
            };
            return actions[this.role.entryAction] || "apply";
        },
        cardActionLabel() {
            if (this.cardAction === "materials") return "我的资料";
            if (this.cardAction === "detail") return "查看审核详情";
            return "提交申请";
        },
        canResubmit() {
            return this.role.entryAction === "VIEW_INITIAL_APPLICATION" &&
                !!this.initialApplication && [3, 4].indexOf(Number(this.initialApplication.applicationStatus)) >= 0;
        },
        cardDescription() {
            if (this.role.entryAction === "VIEW_MATERIALS") {
                if (Number(this.role.currentStatus) === 1) {
                    return "化妆师身份当前已认证；可在我的资料中查看已通过资料与审核提交记录。";
                }
                return "化妆师基础资料已通过；当前身份不可用时资料仅可查看。";
            }
            if (this.role.entryAction === "VIEW_INITIAL_APPLICATION" && this.initialApplication && Number(this.initialApplication.applicationStatus) === 1) {
                return "首次申请正在整体审核中，协议材料和原创化妆作品会一并形成最终结论。";
            }
            if (this.role.entryAction === "VIEW_INITIAL_APPLICATION") {
                return "首次申请尚未通过，请在审核详情中查看处理原因并重新提交。";
            }
                return "完成联系方式、协议材料与 5 张原创化妆作品后，即可提交化妆师首次申请。";
        },
    },
    watch: {
        initialApplicationId: {
            immediate: true,
            handler() {
                this.applicationDetail = null;
                this.detailError = "";
                this.detailDialogVisible = false;
            },
        },
    },
    methods: {
        handleCardAction() {
            if (this.cardAction === "materials") {
                this.$emit("open-materials");
                return;
            }
            if (this.cardAction === "detail") {
                this.openApplicationDetail();
                return;
            }
            this.dialogVisible = true;
        },
        openApplicationDetail() {
            this.detailDialogVisible = true;
            return this.loadApplicationDetail();
        },
        closeApplicationDetail() {
            this.detailDialogVisible = false;
        },
        openResubmit() {
            this.detailDialogVisible = false;
            this.dialogVisible = true;
        },
        loadApplicationDetail() {
            if (!this.initialApplicationId) {
                this.applicationDetail = null;
                this.detailError = "";
                return Promise.resolve();
            }
            this.detailLoading = true;
            this.detailError = "";
            return request.get(API.creatorCenter.makeupArtist.applicationDetail(this.initialApplicationId))
                .then((response) => {
                    if (!isResultSuccess(response)) throw response;
                    this.applicationDetail = normalizeProfessionalApplicationDetail(getResultData(response), "makeupArtist");
                })
                .catch((error) => {
                    this.applicationDetail = null;
                    this.detailError = getResultMessage(error, "申请审核详情暂时无法加载，请稍后刷新");
                })
                .finally(() => {
                    this.detailLoading = false;
                });
        },
        handleApplicationSubmitted() {
            this.dialogVisible = false;
            this.detailDialogVisible = false;
            this.$emit("submitted");
        },
    },
};
</script>

<style scoped>
.makeup-application-card { position: relative; display: grid; grid-template-columns: 128px minmax(0, 1fr); gap: 22px; min-height: 272px; padding: 20px; overflow: hidden; background: #fff; border: 1px solid #efd9dc; border-radius: 24px; box-shadow: 0 14px 30px rgba(98, 62, 106, .07); }
.makeup-application-card::before { position: absolute; top: 0; right: 0; left: 0; height: 6px; content: ""; background: #ffa3b8; }.makeup-card-art { height: 230px; min-height: 230px; overflow: hidden; background: #fff0f4; border-radius: 14px; }.makeup-card-portrait { display: block; width: 100%; height: 100%; object-fit: cover; object-position: center; }.makeup-card-content { display: flex; min-width: 0; flex-direction: column; }.makeup-card-topline { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; }.makeup-card-topline p { margin: 0 0 3px; color: #bd6680; font-size: 10px; font-weight: 800; letter-spacing: .12em; }.makeup-card-topline h3 { margin: 0; color: #3f3158; font-size: 24px; font-weight: 800; letter-spacing: -.02em; }
.makeup-card-badges { display: flex; flex: 0 0 auto; flex-wrap: wrap; justify-content: flex-end; gap: 6px; }.makeup-status-badge, .makeup-campus-badge { display: inline-flex; flex: 0 0 auto; align-items: center; min-height: 26px; padding: 3px 9px; border-radius: 999px; font-size: 11px; font-weight: 800; }.makeup-campus-badge { color: #7957a8; background: #f1eafe; }.makeup-status-badge.ready { color: #a34f69; background: #fff0f4; }.makeup-status-badge.review { color: #916022; background: #fff1c9; }.makeup-status-badge.approved { color: #31735a; background: #e6f7ed; }.makeup-status-badge.rejected { color: #a0475c; background: #fff0f3; }.makeup-status-badge.withdrawn { color: #696276; background: #f0eef2; }
.makeup-card-description { min-height: 42px; margin: 14px 0 8px; color: #746c7e; font-size: 13px; line-height: 1.65; }.makeup-card-history { display: grid; gap: 3px; margin: 0 0 14px; color: #857f76; font-size: 12px; line-height: 1.55; }.makeup-card-history strong { color: #735a69; }.makeup-card-reason { color: #a1495c; }.makeup-card-action-row { display: flex; gap: 8px; margin-top: auto; }.makeup-card-action { width: 100%; min-height: 42px; padding: 9px 14px; color: #fff; background: #d56886; border: 1px solid #d56886; border-radius: 12px; cursor: pointer; font: inherit; font-size: 13px; font-weight: 800; transition: background-color 180ms ease, border-color 180ms ease; }.makeup-card-action:hover { background: #bd5471; border-color: #bd5471; }.makeup-card-action--quiet { color: #b75a75; background: transparent; border-color: #edbdc9; }.makeup-card-action--quiet:hover { color: #fff; background: #d56886; border-color: #d56886; }.makeup-card-action:focus-visible { outline: 0 !important; box-shadow: none !important; }
@media (max-width: 1250px) { .makeup-application-card { grid-template-columns: 110px minmax(0, 1fr); }.makeup-card-art { height: 220px; min-height: 220px; } }
</style>
